import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Plus,
  Save,
  X,
  Users,
  Search
} from 'lucide-react';
import { Alert, AlertColor, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

interface AdminUser {
  _id: string;
  name: string;
  email: string;
  role: string;
  permissions: string[];
  active: boolean;
  createdBy: string;
  createdAt: string;
}

interface FormData {
  name: string;
  email: string;
  password: string;
  role: 'Super Admin' | 'Manager';
  number: string;
  permissions: string[];
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const Roles: React.FC = () => {
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    role: 'Manager',
    number: '',
    permissions: []
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);
  const [filterText, setFilterText] = useState<string>('');
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean, userId: string | null }>({
    open: false,
    userId: null
  });
  const [checkingPermissions, setCheckingPermissions] = useState<boolean>(false);

  // Base URL for API
  const API_BASE_URL = `${API_BASE}/api/roles`;

  // All available permissions based on your routes
  const availablePermissions = [
    { path: '/', label: 'Dashboard' },
    { path: '/users', label: 'Users Management' },
    { path: '/teamDetails', label: 'Team Details' },
    { path: '/teamincome', label: 'Team Income Analytics' },
    { path: '/income', label: 'Personal Income' },
    { path: '/referal', label: 'Referral Program' },
    { path: '/transferDetails', label: 'Transfer History' },
    { path: '/wallet', label: 'Digital Wallet' },
    { path: '/invest', label: 'Investment Portal' },
    { path: '/investHistory', label: 'Investment History' },
    { path: '/deposit', label: 'Deposit Management' },
    { path: '/withdraw', label: 'Withdrawal Requests' },
    { path: '/withdrawData', label: 'Withdrawal Analytics' },
    { path: '/banner', label: 'Banner Management' },
    { path: '/kyc', label: 'KYC Verification' },
    { path: '/ticket', label: 'Support Tickets' },
    { path: '/platinumData', label: 'Platinum Members' },
    { path: '/platinumIncome', label: 'Platinum Income' },
    { path: '/platinumIncomeTeam', label: 'Platinum Team Income' },
    { path: '/notification', label: 'Notification Management' },
    { path: '/appVersion', label: 'App Version Management' },
    { path: '/roles', label: 'Roles & Responsibilities' },
    { path: '/blogs', label: 'Blogs' },
    { path: '/gwccoin', label: 'GWC Coins' },
    { path: '/rank', label: 'Ranks' },
    { path: '/mail', label: 'Mailer' },
    { path: '/incomeStat', label: 'Income Statistics' },
    { path: '/shiftHistory', label: 'Shift History' },
    { path: '/platinumHistory', label: 'Platinum History' }
  ];

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.status === 'success') {
        setAdminUsers(response.data.data.adminRoles);
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to fetch admin users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const checkUserPermissions = async (email: string) => {
    setCheckingPermissions(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/permissions`,
        { email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success' && response.data.data.permissions) {
        // Update form data with the retrieved permissions
        setFormData(prev => ({
          ...prev,
          permissions: response.data.data.permissions
        }));

        showSnackbar('User permissions loaded successfully', 'success');
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to fetch user permissions', 'error');
    } finally {
      setCheckingPermissions(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';

    if (!editingUser) {
      if (!formData.password.trim()) newErrors.password = 'Password is required';
      else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';

      if (!formData.number.trim()) newErrors.number = 'Number is required';
    }

    if (formData.permissions.length === 0) newErrors.permissions = 'Select at least one permission';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePermissionToggle = (path: string) => {
    setFormData(prev => {
      const permissions = [...prev.permissions];

      if (permissions.includes(path)) {
        // Remove permission
        return { ...prev, permissions: permissions.filter(p => p !== path) };
      } else {
        // Add permission
        return { ...prev, permissions: [...permissions, path] };
      }
    });

    // Clear permissions error if any
    if (errors.permissions) {
      setErrors(prev => ({ ...prev, permissions: '' }));
    }
  };

  const handleCreate = async () => {
    if (!validateForm()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API_BASE_URL,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        showSnackbar('Admin user created successfully', 'success');
        fetchAdminUsers();
        resetForm();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to create admin user', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!validateForm() || !editingUser) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_BASE_URL}/${editingUser}`,
        { permissions: formData.permissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === 'success') {
        showSnackbar('Admin user updated successfully', 'success');
        fetchAdminUsers();
        setEditingUser(null);
        resetForm();
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to update admin user', 'error');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.userId) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_BASE_URL}/${deleteDialog.userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 204) {
        showSnackbar('Admin user deleted successfully', 'success');
        fetchAdminUsers();
        setDeleteDialog({ open: false, userId: null });
      }
    } catch (error: any) {
      showSnackbar(error.response?.data?.message || 'Failed to delete admin user', 'error');
    }
  };

  const handleEditClick = (user: AdminUser) => {
    setEditingUser(user._id);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: (user as any).role === 'Viewer' ? 'Manager' : ((user as any).role || 'Manager'),
      number: '',
      permissions: user.permissions
    });
    setShowCreateForm(true);
  };

  const handleCheckPermissions = () => {
    if (formData.email) {
      checkUserPermissions(formData.email);
    } else {
      setErrors(prev => ({ ...prev, email: 'Email is required to check permissions' }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'Manager',
      number: '',
      permissions: []
    });
    setErrors({});
    setShowCreateForm(false);
    setEditingUser(null);
  };

  const showSnackbar = (message: string, severity: AlertColor) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const filteredAdminUsers = adminUsers.filter(user => {
    const searchText = filterText.toLowerCase();
    return (
      user.name.toLowerCase().includes(searchText) ||
      user.email.toLowerCase().includes(searchText)
    );
  });

  return (
    <div className="flex flex-col h-full">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Roles Management</h1>
        <p className="text-gray-600">
          Create and manage admin users with specific access permissions
        </p>
      </div>

      {/* Top Action Bar */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative w-full md:w-64">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search admin users..."
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#523680]"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          {editingUser && (
            <button
              onClick={() => {
                setEditingUser(null);
                setFormData({
                  name: '',
                  email: '',
                  password: '',
                  role: 'Manager',
                  number: '',
                  permissions: []
                });
                setErrors({});
              }}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Plus size={18} />
              Create New Admin
            </button>
          )}
          <button
            onClick={() => {
              if (showCreateForm) {
                resetForm();
              } else {
                // Ensure form is fresh before opening
                setFormData({
                  name: '',
                  email: '',
                  password: '',
                  role: 'Manager',
                  number: '',
                  permissions: []
                });
                setErrors({});
                setEditingUser(null);
                setShowCreateForm(true);
              }
            }}
            className={`flex items-center gap-2 px-4 py-2 ${showCreateForm ? 'bg-gray-500 hover:bg-gray-600' : 'bg-[#523680] hover:bg-[#3f2a60]'} text-white rounded-lg transition-colors`}
          >
            {showCreateForm ? (
              <>
                <X size={18} />
                Cancel
              </>
            ) : (
              <>
                <Plus size={18} />
                Create Admin User
              </>
            )}
          </button>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow-md mb-6 border border-gray-200">
          <h2 className="text-xl font-semibold mb-4">
            {editingUser ? 'Edit Admin User' : 'Create New Admin User'}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!!editingUser}
                className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'
                  } rounded-md focus:outline-none focus:ring-2 focus:ring-[#523680]`}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-500">{errors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="flex">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={!!editingUser}
                  className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'
                    } rounded-l-md focus:outline-none focus:ring-2 focus:ring-[#523680]`}
                />
                {!editingUser && (
                  <button
                    type="button"
                    onClick={handleCheckPermissions}
                    disabled={checkingPermissions}
                    className="px-3 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-r-md hover:bg-gray-200 focus:outline-none"
                  >
                    {checkingPermissions ? 'Checking...' : 'Check Permissions'}
                  </button>
                )}
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">{errors.email}</p>
              )}
            </div>

            {!editingUser && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.password ? 'border-red-500' : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-[#523680]`}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="number"
                    value={formData.number}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border ${errors.number ? 'border-red-500' : 'border-gray-300'
                      } rounded-md focus:outline-none focus:ring-2 focus:ring-[#523680]`}
                  />
                  {errors.number && (
                    <p className="mt-1 text-sm text-red-500">{errors.number}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#523680]"
                  >
                    <option value="Super Admin">Super Admin</option>
                    <option value="Manager">Manager</option>
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Permissions
            </label>

            {errors.permissions && (
              <p className="mb-2 text-sm text-red-500">{errors.permissions}</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {availablePermissions.map((permission) => (
                <div
                  key={permission.path}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${formData.permissions.includes(permission.path)
                    ? 'border-[#523680] bg-[#f5f3fa]'
                    : 'border-gray-200 hover:border-gray-400'
                    }`}
                  onClick={() => handlePermissionToggle(permission.path)}
                >
                  <div className="mr-2">
                    {formData.permissions.includes(permission.path) ? (
                      <CheckCircle className="w-5 h-5 text-[#523680]" />
                    ) : (
                      <div className="w-5 h-5 rounded-full border border-gray-300" />
                    )}
                  </div>
                  <span className="text-sm">{permission.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={resetForm}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={editingUser ? handleUpdate : handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#523680] text-white rounded-md hover:bg-[#3f2a60]"
            >
              <Save size={18} />
              {editingUser ? 'Update Permissions' : 'Create Admin User'}
            </button>
          </div>
        </div>
      )}

      {/* Users View */}
      <div className="bg-transparent">
        {/* Desktop Table View */}
        <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-xl shadow-gray-200/40 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-100">
            <thead className="bg-gray-50/50">
              <tr>
                <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Admin Details</th>
                <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Status</th>
                <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Access</th>
                <th scope="col" className="px-6 py-4 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">Created</th>
                <th scope="col" className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400 italic">Loading admin users...</td></tr>
              ) : filteredAdminUsers.length === 0 ? (
                <tr><td colSpan={5} className="px-6 py-10 text-center text-gray-400 italic">No admin users found</td></tr>
              ) : (
                filteredAdminUsers.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="text-sm font-black text-gray-900 uppercase tracking-tight truncate">{user.name}</div>
                      <div className="text-[10px] text-gray-400 font-bold lowercase truncate">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${user.active ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                        {user.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1">
                        <span className={`w-fit px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${user.role === 'Super Admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {user.role}
                        </span>
                        <span className="text-[10px] text-gray-400 font-bold">{user.permissions.length} PERMISSIONS</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[11px] font-black text-gray-600 uppercase tracking-widest">{user.createdBy || 'SYSTEM'}</div>
                      <div className="text-[9px] text-gray-400 font-bold uppercase">{user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-GB') : 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => handleEditClick(user)} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors">
                          <Edit className="w-5 h-5" />
                        </button>
                        <button onClick={() => setDeleteDialog({ open: true, userId: user._id })} className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors">
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-2xl border border-gray-100 animate-pulse h-32"></div>
            ))
          ) : filteredAdminUsers.map((user) => (
            <div key={user._id} className="bg-white p-4 rounded-2xl shadow-lg shadow-gray-200/50 border border-gray-100 relative overflow-hidden group">
              <div className={`absolute top-0 right-0 w-20 h-20 rounded-bl-full -mr-8 -mt-8 opacity-10 transition-transform group-hover:scale-110 ${user.role === 'Super Admin' ? 'bg-purple-600' : 'bg-blue-600'}`}></div>

              <div className="flex justify-between items-start mb-3 relative z-10">
                <div className="min-w-0 pr-4">
                  <h3 className="text-sm font-black text-gray-900 uppercase tracking-tight truncate mb-0.5">{user.name}</h3>
                  <p className="text-[9px] text-gray-400 font-bold lowercase truncate">{user.email}</p>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-tighter shrink-0 ${user.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {user.active ? 'Active' : 'Offline'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50/50 rounded-xl border border-gray-100/50 mb-4 relative z-10">
                <div>
                  <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5 tracking-widest">Role & Access</span>
                  <p className={`text-[10px] font-black uppercase tracking-tighter ${user.role === 'Super Admin' ? 'text-purple-600' : 'text-blue-600'}`}>{user.role}</p>
                  <p className="text-[8px] text-gray-400 font-bold uppercase mt-0.5">{user.permissions.length} Modules</p>
                </div>
                <div>
                  <span className="block text-[8px] text-gray-400 font-bold uppercase mb-0.5 tracking-widest">Created By</span>
                  <p className="text-[10px] font-black text-gray-700 uppercase truncate">{user.createdBy || 'SYSTEM'}</p>
                </div>
              </div>

              <div className="flex gap-2 relative z-10">
                <button
                  onClick={() => handleEditClick(user)}
                  className="flex-1 py-2 text-[10px] font-black uppercase tracking-widest text-blue-600 bg-blue-50 rounded-lg border border-blue-100 active:scale-95 transition-all"
                >
                  Edit Access
                </button>
                <button
                  onClick={() => setDeleteDialog({ open: true, userId: user._id })}
                  className="px-4 py-2 text-[10px] font-black uppercase tracking-widest text-red-400 bg-red-50 rounded-lg border border-red-50 active:scale-95 transition-all"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this admin user? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, userId: null })}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Roles;